/**
 * O campo **Empresa** com sugestão (13.1, FR-3 emendado) — escolher uma Empresa cadastrada, ou
 * pedir para criar uma nova.
 *
 * Nasceu para o "Novo negócio", onde o nome da Empresa era texto livre e todo envio criava uma
 * Empresa nova (a segunda Veluplast do chamado de 21/10, P10). Agora o campo **sugere** as Empresas
 * que quem digita enxerga — `GET /empresas?busca=`, que a RLS recorta (FR-30) e que desde a 13.1
 * ignora acento e pontuação — e a **última opção é sempre "Criar nova empresa"**, que devolve o
 * texto digitado para quem montou o campo abrir a ficha do "+ Empresa". Sugerir não é bloquear
 * (FR-3): a duplicata continua possível, só deixa de nascer sem a usuária ver a existente. Ele é
 * reusável: "Trocar a Empresa" e "Vincular" (EXPERIENCE.md) o montam igual — e o "Vincular a uma
 * Empresa existente" do painel do contato (13.3) o monta **sem** `aoCriarNova`, e a última opção
 * não aparece.
 *
 * **Combobox ARIA** (`role="combobox"`, `aria-expanded`, `aria-controls`, `aria-activedescendant`,
 * `aria-autocomplete="list"`) com região viva do número de resultados. ↑ ↓ movem, Enter escolhe,
 * Esc fecha — e **com a lista aberta** Enter e Esc fazem `preventDefault`: o Enter não envia o
 * formulário do Negócio, e o Esc não abre o "Descartar o negócio novo?" da janela (que respeita
 * `defaultPrevented`). O `mousedown`/`pointerdown` das opções também é prevenido, para o campo não
 * perder o foco antes de a escolha acontecer.
 *
 * **A consulta só sai com termo**: vazio (ou só espaço) não pergunta nada, e a lista mostra só
 * "Criar nova empresa". O termo espera 300 ms de digitação parada e é cortado em `TAMANHO_DA_BUSCA`.
 * **Desde a 13.14a (DW-373) vêm todas, de 10 em 10, a mais parecida primeiro** (`ordem=semelhanca`:
 * o nome igual, o começo, a palavra inteira, o resto): "Mostrar mais" é a penúltima opção enquanto
 * houver página, acrescenta as 10 seguintes sem fechar a lista e leva a ativa à primeira nova; em
 * voo, ela fica desabilitada; a falha dela vira "Tentar de novo", com as carregadas na lista. A nota
 * diz "10 de 37 encontradas". O resultado velho (`isPlaceholderData`, ou a digitação ainda não buscada)
 * marca a lista `aria-busy` e não pode ser escolhido. Falha de rede ignora os dados e avisa no
 * campo; a recusa do serviço aparece **verbatim**, distinta do aviso de rede.
 *
 * Cada sugestão mostra a razão social e, abaixo, o CNPJ/CPF formatado (ou "sem CNPJ"), cidade/UF
 * e o nome do Responsável — é o que separa duas homônimas. **Paridade de celular (NFR-1):** a lista
 * tem altura máxima e rola até a opção ativa nas duas larguras.
 */

import {
  type KeyboardEvent,
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { ErroDaApi } from '@/api/erros'
import { useResponsaveis } from '@/modulos/identidade/api'
import { LinhaDoRotulo } from '@/shell/MarcaDeObrigatorio'
import {
  type EmpresaNaLista,
  SUGESTOES_POR_PAGINA,
  TAMANHO_DA_BUSCA,
  useSugestoesDeEmpresas,
} from './api'
import { conteinerDoPainel, usePosicaoDoPainel } from './etiquetas'
import { nomeExibidoDaEmpresa, razaoSocialQueDifere } from './nome-da-empresa'
import './campo-de-empresa.css'

/** Quantas sugestões cada página traz (13.14a, DW-373: "de 10 em 10"; eram 8 desde a P-A). */
export const LIMITE_DE_SUGESTOES = SUGESTOES_POR_PAGINA

/** Quanto a digitação precisa parar antes de a busca sair (P-A). */
export const ESPERA_DA_BUSCA_EM_MS = 300

/** O teto da razão social — espelha `TAMANHO_DA_RAZAO_SOCIAL` de `dominio/empresa.py`. */
export const TAMANHO_DA_RAZAO_SOCIAL = 200
/** O teto do nome fantasia — espelha `TAMANHO_DO_NOME_FANTASIA` de `dominio/empresa.py` (13.7). */
export const TAMANHO_DO_NOME_FANTASIA = 200

export const ROTULO_DO_CAMPO_DE_EMPRESA = 'Empresa'
export const CRIAR_NOVA_EMPRESA = 'Criar nova empresa'
/** A penúltima opção, enquanto houver página (13.14a). */
export const MOSTRAR_MAIS = 'Mostrar mais'
export const CARREGANDO_MAIS = 'Carregando mais…'
/** A página seguinte falhou: as carregadas ficam, e a opção tenta de novo. */
export const TENTAR_MAIS_DE_NOVO = 'Não foi possível carregar mais. Tentar de novo'
export const SEM_CNPJ = 'sem CNPJ'

/** Falha de rede (ou resposta sem envelope): o texto é do SPA, porque o serviço não falou. */
export const AVISO_DE_BUSCA_INDISPONIVEL =
  'Não foi possível buscar as Empresas agora. Você ainda pode criar uma nova.'

const BUSCANDO = 'Buscando…'

/**
 * Sem "Criar nova empresa" (13.3), a busca que não achou nada precisa dizer isso: com a opção de
 * criar, ela é sempre a última linha, e a lista nunca fica vazia.
 */
export const NENHUMA_EMPRESA_ENCONTRADA = 'Nenhuma Empresa encontrada.'

/** A falha de rede sem "Criar nova empresa": o aviso não promete o que o campo não oferece. */
export const AVISO_DE_BUSCA_INDISPONIVEL_SEM_CRIAR =
  'Não foi possível buscar as Empresas agora. Tente de novo em instantes.'

/** A linha de "há mais do que a lista mostra" (13.14a): as carregadas e o total. */
export function muitasEncontradas(carregadas: number, total: number): string {
  return `${String(carregadas)} de ${String(total)} encontradas`
}

function anuncioDe(quantas: number): string {
  if (quantas === 0) {
    return 'Nenhuma Empresa encontrada'
  }
  return quantas === 1 ? '1 Empresa encontrada' : `${String(quantas)} Empresas encontradas`
}

/** O documento como a operação o lê: CNPJ `00.000.000/0000-00`, CPF `000.000.000-00`. */
export function documentoNaSugestao(documento: string | null): string {
  if (documento === null || documento === '') {
    return SEM_CNPJ
  }
  if (/^\d{14}$/.test(documento)) {
    return documento.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  }
  if (/^\d{11}$/.test(documento)) {
    return documento.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
  }
  return documento
}

/**
 * A Empresa escolhida: o `id` que vai no Negócio e o **nome exibido** que o campo mostra (13.7: o
 * nome fantasia, e a razão social sem ele). Chamava-se `razao_social` até a 13.7 — e passou a
 * `nome` porque um campo com esse nome guardando a marca seria um nome que mente.
 */
export interface EmpresaEscolhida {
  id: string
  nome: string
}

export interface PropriedadesDoCampoDeEmpresa {
  /** O `id` do `<input>` — o `<label>` se associa por ele. */
  id: string
  escolhida: EmpresaEscolhida | null
  /** `null` quando a pessoa apaga a escolha (qualquer tecla depois de escolher). */
  aoEscolher: (empresa: EmpresaEscolhida | null) => void
  /**
   * "Criar nova empresa": devolve o texto digitado, aparado, para quem abre a ficha.
   *
   * **Opcional desde a 13.3**: o "Vincular a uma Empresa existente" do painel do contato só escolhe
   * entre as que existem — criar tem botão próprio ali. Sem ele, a última opção não aparece, e com
   * o termo vazio não há lista para abrir.
   */
  aoCriarNova?: (texto: string) => void
  /** A recusa local de quem monta o campo (ex.: "Escolha uma Empresa da lista ou crie uma nova."). */
  erro?: string | null
  /** O texto do campo enquanto a Empresa escolhida ainda está sendo lida. */
  placeholder?: string
  refDoCampo?: Ref<HTMLInputElement>
  /**
   * A escolha veio de fora (a conversa de WhatsApp), e não da digitação: a janela do Negócio não
   * conta o campo como "algo digitado" (`data-nao-conta`).
   */
  daConversa?: boolean
  /**
   * O `*` vermelho no rótulo e o `aria-required` no campo (13.15). Opcional, porque o vínculo da
   * Pessoa solta, a troca de Empresa e o "Incluir filial" o montam sem ser campo obrigatório de
   * formulário; o "+ Pessoa" e o Novo negócio o passam.
   */
  obrigatorio?: boolean
  /**
   * 13.20b2: a lista em portal (`position: fixed`, `--camada-painel-flutuante`, posicionada por
   * `usePosicaoDoPainel`) — a do campo "Grupo" da 13.18. Dentro de um diálogo de corpo curto (o
   * "Definir a empresa do negócio"), a lista absoluta seria cortada pelo `overflow` do corpo: em
   * portal, ela fica por cima ("lista por cima, nunca cortada"). O portal é o diálogo do campo
   * (`conteinerDoPainel`), e fora de diálogo o `document.body`.
   */
  flutuante?: boolean
}

/** Uma linha da lista: as sugestões, "Mostrar mais" quando há página, e por último "Criar nova empresa". */
type Opcao = { tipo: 'empresa'; empresa: EmpresaNaLista } | { tipo: 'mais' } | { tipo: 'criar' }

/** O termo como vai à rede: aparado e cortado no teto do serviço. */
function termoDe(texto: string): string {
  return texto.trim().slice(0, TAMANHO_DA_BUSCA)
}

function mensagemDaFalha(falha: unknown, comCriar: boolean): string {
  if (falha instanceof ErroDaApi && falha.codigo !== 'erro_desconhecido') {
    return falha.message
  }
  return comCriar ? AVISO_DE_BUSCA_INDISPONIVEL : AVISO_DE_BUSCA_INDISPONIVEL_SEM_CRIAR
}

export function CampoDeEmpresa({
  id,
  escolhida,
  aoEscolher,
  aoCriarNova,
  erro = null,
  placeholder,
  refDoCampo,
  daConversa = false,
  obrigatorio = false,
  flutuante = false,
}: PropriedadesDoCampoDeEmpresa) {
  const base = useId()
  const idDaLista = `${base}-lista`
  const idDoErro = `${base}-erro`
  const idDaFalha = `${base}-falha`
  const idDoAnuncio = `${base}-anuncio`

  const [texto, setTexto] = useState('')
  const [aberta, setAberta] = useState(false)
  /*
   * A opção ativa vale **só para a lista em que foi escolhida**: ela guarda a chave da consulta
   * (termo e `dataUpdatedAt`) junto do índice. Chegou dado novo — outro termo, ou o mesmo termo
   * relido —, a chave muda e a ativa volta a nenhuma, sem efeito de cópia: o Enter nunca escolhe
   * uma linha que a pessoa não viu sob o cursor.
   */
  const [marcada, setMarcada] = useState<{ chave: string; indice: number }>({
    chave: '',
    indice: -1,
  })
  const [termo, setTermo] = useState('')
  const lista = useRef<HTMLUListElement>(null)
  /*
   * 13.20b2: a âncora e a caixa da lista flutuante, e onde ela mora (o diálogo do campo). A âncora
   * é o `<input>` achado dentro do campo, e não um segundo `ref` nele: o `refDoCampo` continua de
   * quem monta, sem mescla.
   */
  const campo = useRef<HTMLDivElement>(null)
  const entrada = useMemo<RefObject<HTMLInputElement | null>>(
    () => ({
      get current() {
        return campo.current?.querySelector<HTMLInputElement>('.campo-de-empresa__entrada') ?? null
      },
    }),
    [],
  )
  const popup = useRef<HTMLDivElement>(null)
  const [conteiner, setConteiner] = useState<HTMLElement | null>(null)
  /* O contêiner da lista flutuante é resolvido na montagem (code review da 13.20b2, B12): o
     `aria-controls` do campo aponta para uma lista que existe desde o começo, e não só depois do
     primeiro foco. */
  const refDoCampoInterno = useCallback(
    (elemento: HTMLDivElement | null) => {
      campo.current = elemento
      if (flutuante && elemento !== null) {
        setConteiner(conteinerDoPainel(elemento))
      }
    },
    [flutuante],
  )

  const digitado = termoDe(texto)
  useEffect(() => {
    const espera = setTimeout(() => {
      setTermo(digitado)
    }, ESPERA_DA_BUSCA_EM_MS)
    return () => {
      clearTimeout(espera)
    }
  }, [digitado])

  const consulta = useSugestoesDeEmpresas(termo, { habilitada: termo !== '' })
  const chaveDaLista = `${termo}|${String(consulta.dataUpdatedAt)}`
  const ativa = marcada.chave === chaveDaLista ? marcada.indice : -1
  const setAtiva = (indice: number) => {
    setMarcada({ chave: chaveDaLista, indice })
  }
  const equipe = useResponsaveis()
  const nomes = new Map((equipe.data ?? []).map((pessoa) => [pessoa.id, pessoa.nome]))

  const comTermo = termo !== '' && digitado !== ''
  /* A falha da **primeira** página é a do campo; a da seguinte fica na opção "Mostrar mais". */
  const falhou = comTermo && consulta.isError && !consulta.isFetchNextPageError
  const paginas = comTermo && !falhou ? (consulta.data?.pages ?? []) : []
  /* Sem repetir: com escrita entre uma página e outra, o `OFFSET` pode devolver a mesma ficha. */
  const sugestoes = [
    ...new Map(paginas.flatMap((pagina) => pagina.itens).map((item) => [item.id, item])).values(),
  ]
  const total = paginas.at(-1)?.total ?? 0
  const temMais = comTermo && !falhou && consulta.hasNextPage
  /* Resultado velho: o que está na tela é de outro termo — o anterior, guardado pelo
     `keepPreviousData`, ou o que ainda espera os 300 ms da digitação. */
  const desatualizada = comTermo && (consulta.isPlaceholderData || digitado !== termo)
  const buscando = digitado !== '' && (digitado !== termo || consulta.isFetching)

  const opcoes: Opcao[] = [
    ...sugestoes.map((empresa) => ({ tipo: 'empresa' as const, empresa })),
    ...(temMais ? [{ tipo: 'mais' as const }] : []),
    ...(aoCriarNova === undefined ? [] : [{ tipo: 'criar' as const }]),
  ]
  const escolhivel = (opcao: Opcao) =>
    opcao.tipo === 'criar' ||
    (opcao.tipo === 'mais' ? !desatualizada && !consulta.isFetchingNextPage : !desatualizada)
  /* Sem "Criar nova empresa", a lista só abre com termo e sem falha (a falha tem aviso próprio,
     embaixo do campo): vazia, ela diria nada a quem olha. */
  const temOQueOferecer = aoCriarNova !== undefined || (digitado !== '' && !falhou)
  const listaAberta = aberta && escolhida === null && temOQueOferecer
  /* A busca terminou, sem sugestão nenhuma e sem a linha de criar: a lista diz isso. */
  const nadaEncontrado =
    aoCriarNova === undefined && comTermo && !desatualizada && !buscando && opcoes.length === 0

  useEffect(() => {
    if (!listaAberta || ativa < 0) {
      return
    }
    // `scrollIntoView` não existe no jsdom: a chamada é opcional, e no celular é ela que mantém a
    // opção ativa visível dentro da altura máxima da lista.
    lista.current?.querySelector<HTMLElement>(`[data-indice="${String(ativa)}"]`)?.scrollIntoView?.({
      block: 'nearest',
    })
  }, [ativa, listaAberta])

  function escolher(opcao: Opcao) {
    if (!escolhivel(opcao)) {
      return
    }
    if (opcao.tipo === 'mais') {
      // A lista fica aberta, e a ativa vai à primeira das que chegarem (o índice dela é o de agora).
      const primeiraNova = sugestoes.length
      void consulta.fetchNextPage().then((resultado) => {
        if (!resultado.isSuccess) {
          return
        }
        const carregadas = new Set(
          resultado.data.pages.flatMap((pagina) => pagina.itens).map((item) => item.id),
        ).size
        // A página que só trouxe repetidas (o `OFFSET` com escrita no meio) não move a ativa: o
        // índice de "agora" seria o de "Mostrar mais" ou de "Criar nova empresa".
        setMarcada({
          chave: `${termo}|${String(resultado.dataUpdatedAt)}`,
          indice: carregadas > primeiraNova ? primeiraNova : Math.max(primeiraNova - 1, 0),
        })
      })
      return
    }
    setAberta(false)
    setAtiva(-1)
    if (opcao.tipo === 'criar') {
      // Cortado no teto do nome fantasia (13.14a, DW-420: o texto vai para ele), por ponto de código
      // — o corte por unidade UTF-16 partiria um emoji ao meio.
      aoCriarNova?.(Array.from(texto.trim()).slice(0, TAMANHO_DO_NOME_FANTASIA).join(''))
      return
    }
    aoEscolher({ id: opcao.empresa.id, nome: nomeExibidoDaEmpresa(opcao.empresa) })
  }

  function mover(passo: 1 | -1) {
    const indices = opcoes.map((_, indice) => indice).filter((indice) => {
      const opcao = opcoes[indice]
      return opcao !== undefined && escolhivel(opcao)
    })
    if (indices.length === 0) {
      return
    }
    const atual = indices.indexOf(ativa)
    const proxima =
      atual === -1
        ? passo === 1
          ? 0
          : indices.length - 1
        : (atual + passo + indices.length) % indices.length
    setAtiva(indices[proxima] ?? -1)
  }

  function aoTeclar(evento: KeyboardEvent<HTMLInputElement>) {
    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      evento.preventDefault()
      if (!listaAberta) {
        setAberta(true)
        return
      }
      mover(evento.key === 'ArrowDown' ? 1 : -1)
      return
    }
    if (!listaAberta) {
      return
    }
    if (evento.key === 'Enter') {
      // Com a lista aberta o Enter é da lista, e nunca do formulário de quem montou o campo.
      evento.preventDefault()
      const opcao = opcoes[ativa]
      if (opcao !== undefined) {
        escolher(opcao)
        return
      }
      // Nenhuma ativa: o Enter fecha a lista (e continua sem enviar o formulário).
      setAberta(false)
      return
    }
    if (evento.key === 'Escape') {
      // Marcado: a janela do Negócio ouve `keydown` no `document` e respeita `defaultPrevented`.
      evento.preventDefault()
      setAberta(false)
      setAtiva(-1)
    }
  }

  const descricao =
    [erro === null ? null : idDoErro, falhou ? idDaFalha : null].filter(Boolean).join(' ') ||
    undefined
  const ativaNaLista = listaAberta && ativa >= 0 && ativa < opcoes.length
  const posicao = usePosicaoDoPainel(flutuante && listaAberta, entrada, popup, {
    daLarguraDaAncora: true,
  })

  /* A lista suspensa: dentro do campo, ou — `flutuante` — em portal no diálogo dele. */
  const listaSuspensa = (
    <div
      ref={popup}
      className={
        flutuante
          ? 'campo-de-empresa__popup campo-de-empresa__popup--flutuante'
          : 'campo-de-empresa__popup'
      }
      hidden={!listaAberta}
      //: Flutuante, antes de medir a lista existe sem aparecer: a primeira posição é a calculada.
      style={flutuante ? (posicao ?? { top: 0, left: 0, visibility: 'hidden' }) : undefined}
    >
      <ul
        ref={lista}
        id={idDaLista}
        hidden={opcoes.length === 0}
        className="campo-de-empresa__lista"
        role="listbox"
        aria-label={ROTULO_DO_CAMPO_DE_EMPRESA}
        aria-busy={desatualizada ? true : undefined}
      >
        {opcoes.map((opcao, indice) => {
          const desabilitada = !escolhivel(opcao)
          const props = {
            id: `${base}-opcao-${String(indice)}`,
            role: 'option' as const,
            'data-indice': indice,
            'aria-selected': indice === ativa,
            'aria-disabled': desabilitada ? true : undefined,
            'data-ativa': indice === ativa ? 'sim' : undefined,
            onMouseDown: (evento: { preventDefault: () => void }) => {
              evento.preventDefault()
            },
            onPointerDown: (evento: { preventDefault: () => void }) => {
              evento.preventDefault()
            },
            onClick: () => {
              escolher(opcao)
            },
          }
          if (opcao.tipo === 'criar') {
            return (
              <li key="criar" className="campo-de-empresa__opcao campo-de-empresa__criar" {...props}>
                {CRIAR_NOVA_EMPRESA}
              </li>
            )
          }
          if (opcao.tipo === 'mais') {
            return (
              <li key="mais" className="campo-de-empresa__opcao campo-de-empresa__mais" {...props}>
                {consulta.isFetchingNextPage
                  ? CARREGANDO_MAIS
                  : consulta.isFetchNextPageError
                    ? TENTAR_MAIS_DE_NOVO
                    : MOSTRAR_MAIS}
              </li>
            )
          }
          const { empresa } = opcao
          const lugar = [empresa.cidade, empresa.uf].filter(Boolean).join('/')
          const responsavel =
            empresa.responsavel_id === null ? null : (nomes.get(empresa.responsavel_id) ?? null)
          /* 13.7 (D-9): a opção pelo nome exibido; a razão social no detalhe quando o nome
             fantasia é outra coisa — a busca também acha por ela, e é como se distingue a
             filial. */
          const razaoSocial = razaoSocialQueDifere(empresa.razao_social, empresa.nome_fantasia)
          return (
            <li key={empresa.id} className="campo-de-empresa__opcao" {...props}>
              <span className="campo-de-empresa__nome">{nomeExibidoDaEmpresa(empresa)}</span>
              <span className="campo-de-empresa__detalhe">
                {[razaoSocial, documentoNaSugestao(empresa.documento), lugar, responsavel]
                  .filter((parte): parte is string => parte !== null && parte !== '')
                  .join(' · ')}
              </span>
            </li>
          )
        })}
      </ul>
      {total > sugestoes.length ? (
        <p className="campo-de-empresa__nota">{muitasEncontradas(sugestoes.length, total)}</p>
      ) : null}
      {buscando && !falhou ? <p className="campo-de-empresa__nota">{BUSCANDO}</p> : null}
      {nadaEncontrado ? (
        <p className="campo-de-empresa__nota">{NENHUMA_EMPRESA_ENCONTRADA}</p>
      ) : null}
    </div>
  )

  return (
    <div className="campo-de-empresa" ref={refDoCampoInterno}>
      <LinhaDoRotulo obrigatorio={obrigatorio}>
        <label className="campo-de-empresa__rotulo" htmlFor={id}>
          {ROTULO_DO_CAMPO_DE_EMPRESA}
        </label>
      </LinhaDoRotulo>
      <input
        ref={refDoCampo}
        id={id}
        className="campo-de-empresa__entrada"
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={listaAberta}
        aria-controls={idDaLista}
        aria-activedescendant={ativaNaLista ? `${base}-opcao-${String(ativa)}` : undefined}
        aria-describedby={descricao}
        aria-invalid={erro === null ? undefined : true}
        aria-required={obrigatorio ? true : undefined}
        data-nao-conta={daConversa && escolhida !== null ? 'sim' : undefined}
        placeholder={placeholder}
        value={escolhida?.nome ?? texto}
        onFocus={(evento) => {
          if (flutuante) {
            setConteiner(conteinerDoPainel(evento.currentTarget))
          }
          if (escolhida === null) {
            setAberta(true)
          }
        }}
        onBlur={() => {
          setAberta(false)
          setAtiva(-1)
        }}
        onKeyDown={aoTeclar}
        onChange={(evento) => {
          // Qualquer tecla depois de escolher apaga a escolha e reabre a lista com o que ficou.
          if (escolhida !== null) {
            aoEscolher(null)
          }
          setTexto(evento.target.value)
          setAtiva(-1)
          setAberta(true)
        }}
      />
      {flutuante
        ? conteiner === null
          ? null
          : createPortal(listaSuspensa, conteiner)
        : listaSuspensa}
      {/* A região viva: o número de resultados, para quem não vê a lista se abrir. */}
      <span id={idDoAnuncio} className="campo-de-empresa__so-para-leitor" role="status">
        {listaAberta && comTermo && !desatualizada && !falhou && consulta.isSuccess
          ? total > sugestoes.length
            ? `${anuncioDe(sugestoes.length)}. ${muitasEncontradas(sugestoes.length, total)}`
            : anuncioDe(sugestoes.length)
          : ''}
      </span>
      {falhou ? (
        <p className="campo-de-empresa__aviso" id={idDaFalha}>
          {mensagemDaFalha(consulta.error, aoCriarNova !== undefined)}
        </p>
      ) : null}
      {erro === null ? null : (
        <p className="campo-de-empresa__erro" id={idDoErro} role="alert">
          {erro}
        </p>
      )}
    </div>
  )
}
